import i18n from 'app/i18/i18'
import { RoutingPaths } from 'app/routes/routes'

import {
  getAviaStatus,
  getBeketStatus,
  getExcursionsStatus,
  getKyrgyzstanStatus,
  getToursStatus,
} from 'entities/common/my-ticket/lib/getMyOrdersStatuses'
import type {
  MyOrderCardProps,
  MyOrderListCardProps,
  OrderGroup,
  OrdersResponse,
  TourDocumentOrderDto,
} from 'entities/common/my-ticket/model/types'
import { calculateNights, getOrderGuestsCount } from 'entities/kyrgyzstan'

import { MyTicketItem } from 'shared/api/MyTicketsModel'
import { OrdersBeket } from 'shared/api/beket/my-orders/model'
import { MyOrders } from 'shared/api/excursions/list/model'
import { InsuranceOrderDto } from 'shared/api/insurance'
import { OrderData } from 'shared/api/kyrgyzstan/orders/model'
import { TourAppeal } from 'shared/api/tours/buy-ticket/model'
import { UmrahMyOrder } from 'shared/api/umrah/buy-tour'
import { DATE_FORMAT_LIST } from 'shared/constants/dateFormatList'
import { IS_DIGITAL_TOUR_SUPPORT_ENABLED } from 'shared/constants/tours'
import { dateFormat, differenceDate } from 'shared/lib/dateFormat'
import { prepareTicketsItems } from 'shared/lib/ticketRender'
import { AllIcons } from 'shared/ui/app-icon/types'

import { MyOrderSectionType, MyOrdersSection } from '../model/models'

import { mapInsuranceOrder } from './mapInsuranceOrder'
import { mapTourDocumentOrder } from './mapTourDocumentOrder'

export interface OrderCategorySection {
  categoryLabel: string
  cards: MyOrderListCardProps[]
}

export const mapKyrgyzstanOrder = (item: OrderData): MyOrderCardProps => {
  const statusConfig = getKyrgyzstanStatus(item.status)

  const startFormatted = dateFormat(
    item.start_date,
    DATE_FORMAT_LIST.DATE_MONTH_SHORT,
  )
  const endFormatted = dateFormat(
    item.end_date,
    DATE_FORMAT_LIST.DATE_MONTH_SHORT,
  )

  const nightsCount = calculateNights(item.start_date, item.end_date)

  const nights = i18n.t('kyrgyzstan_block.search_result.nights', {
    count: nightsCount,
  })

  const visitors = i18n.t('kyrgyzstan_block.search_result.guests', {
    count: getOrderGuestsCount(item),
  })

  const details = `${startFormatted} – ${endFormatted}, ${nights}, ${visitors}`

  return {
    id: item.id,
    imageSrc: item.realty_object.parent_image_url,
    title: item.realty_object.parent_name || item.realty_object.name,
    details,
    price: String(item.total_price),
    detailPath: RoutingPaths.kyrgyzstan.myOrders.detail.path(item.id),
    status: {
      config: statusConfig,
      info: {
        category: MyOrdersSection.kyrgyzstan
          .key as keyof typeof MyOrdersSection,
        status: item.status,
        start_date: item.start_date,
        end_date: item.end_date,
        payment_status: item.payment_status,
      },
    },
  }
}

export const mapToursOrder = (item: TourAppeal): MyOrderCardProps => {
  const statusConfig = getToursStatus(item.status)

  const session = item.hotel_search_session
  const startFormatted = dateFormat(
    session.checkin_date,
    DATE_FORMAT_LIST.DATE_MONTH_SHORT,
  )
  const endFormatted = dateFormat(
    session.checkout_date,
    DATE_FORMAT_LIST.DATE_MONTH_SHORT,
  )

  const nights = i18n.t('tours_block.night', {
    count: session.nights,
  })

  const tourists = i18n.t('my_orders_block.count_passengers', {
    count: session.adult + session.child,
  })

  const details = `${startFormatted} – ${endFormatted}, ${nights}, ${tourists}`

  return {
    id: item.id,
    imageSrc: item.hotel?.hotel_images?.[0]?.src,
    title: `${item.hotel.region_name}, ${item.hotel.hotel_name}`,
    details,
    price: item.price,
    detailPath: RoutingPaths.tours.myOrderDetails.path(item.id),
    status: {
      config: statusConfig,
    },
  }
}

export const mapAviaOrder = (
  item: MyTicketItem,
  isArchive: boolean,
): MyOrderCardProps => {
  const statusConfig = getAviaStatus(item.status)

  const ticket = prepareTicketsItems(item)

  const price = String(
    ticket?.price_with_promo_code || ticket.mplus_amount || ticket?.price,
  )

  const firstSegment = ticket?.segments?.at(0)
  const returnSegment = ticket?.segments?.at(1)

  let title = `${firstSegment?.from?.name} - ${firstSegment?.to?.name}`
  let details = `${firstSegment?.from?.date} `

  if (returnSegment) {
    details += `– ${returnSegment?.from?.date}`
    title += ` - ${returnSegment?.to?.name}`
  }

  details += ` • ${i18n.t('pass_count', { count: ticket.passengers })}`

  const type = isArchive ? 'archive' : 'active'

  return {
    id: item.id,
    imageSrc: firstSegment?.carrier?.[0]?.image,
    title,
    details,
    price,
    detailPath: RoutingPaths.avia.myTickets.detail.path(
      item?.id,
      item?.billing_number?.toString() ?? '',
      type,
    ),
    status: {
      config: statusConfig,
    },
  }
}

export const mapExcursionOrder = (item: MyOrders): MyOrderCardProps => {
  const statusConfig = getExcursionsStatus(item.status)

  const exc = item?.excursion
  const details = `${dateFormat(exc?.departure_date, DATE_FORMAT_LIST.DATE_MONTH_SHORT)} • ${i18n.t('participant_count', { count: item?.people_quantity })}`

  return {
    id: item?.id,
    imageSrc: exc?.preview_image,
    title: exc?.name,
    details,
    price: item?.total_price,
    detailPath: RoutingPaths.excursion.myOrder.detail.path(String(item?.id)),
    status: {
      config: statusConfig,
    },
  }
}

export const mapBusOrder = (
  item: OrdersBeket,
  isArchive: boolean,
): MyOrderCardProps => {
  const statusConfig = getBeketStatus(item.status)

  const route = item?.travel_details?.route
  const fromCity = route?.departure_station?.city ?? ''
  const toCity = route?.arrival_station?.city ?? ''
  const title = `${fromCity} → ${toCity}`
  const dateStr = dateFormat(
    item.travel_details?.departure_datetime,
    DATE_FORMAT_LIST.DATE_MONTH_SHORT,
  )
  const details = `${dateStr} • ${i18n.t('pass_count', { count: item?.passengers_count })}`

  return {
    title,
    details,
    id: item?.order_id,
    price: item?.total_price,
    icon: AllIcons.BUS_DISABLED,
    detailPath: RoutingPaths.beket.myTickets.detail.path(
      item?.order_id,
      isArchive,
    ),
    status: {
      config: statusConfig,
    },
  }
}

export const mapUmrahOrder = (item: UmrahMyOrder): MyOrderCardProps => {
  const dateStart = item?.start_date
  const dateEnd = item?.end_date

  const days = differenceDate({
    dateStart,
    dateEnd,
    type: 'day',
  })

  const date = i18n.t('umrah_block.dates', {
    start: dateFormat(dateStart, DATE_FORMAT_LIST.FULL_DATE_SHORT_YEAR),
    end: dateFormat(dateEnd, DATE_FORMAT_LIST.FULL_DATE_SHORT_YEAR),
    count: days,
  })
  const passengers = i18n.t('umrah_block.pilgrims_count', {
    count: item?.number_of_pilgrims,
  })

  const details = `${date} • ${passengers}`

  return {
    id: item?.id,
    imageSrc: item?.logo,
    title: item?.name,
    details,
    price: item?.total_price,
    detailPath: RoutingPaths.umrah.myOrderDetails.path(item?.id),
  }
}

export const mapOrdersToCategories = (
  orders: OrdersResponse | undefined,
  isActive = true,
): OrderCategorySection[] => {
  if (!orders?.length) return []

  const knownCategories = new Set(
    Object.keys(MyOrdersSection) as MyOrderSectionType[],
  )

  return orders
    .flat()
    .filter(
      (group) =>
        group.data?.length &&
        knownCategories.has(group.category as MyOrderSectionType) &&
        (IS_DIGITAL_TOUR_SUPPORT_ENABLED ||
          group.category !== MyOrdersSection.tour_document.key),
    )
    .map((group: OrderGroup) => {
      const section = MyOrdersSection[group.category as MyOrderSectionType]
      const categoryLabel = i18n.t(section?.name)

      const cards: MyOrderListCardProps[] = (group?.data as unknown[]).map(
        (item: unknown): MyOrderListCardProps => {
          switch (group.category) {
            case MyOrdersSection.kyrgyzstan.key:
              return mapKyrgyzstanOrder(item as OrderData)
            case MyOrdersSection.tour_appeal.key:
            case MyOrdersSection.tours.key:
              return mapToursOrder(item as TourAppeal)
            case MyOrdersSection.tour_document.key:
              return mapTourDocumentOrder(item as TourDocumentOrderDto)
            case MyOrdersSection.avia.key:
              return mapAviaOrder(item as MyTicketItem, !isActive)
            case MyOrdersSection.excursion.key:
              return mapExcursionOrder(item as MyOrders)
            case MyOrdersSection.bus.key:
              return mapBusOrder(item as OrdersBeket, !isActive)
            case MyOrdersSection.umra.key:
              return mapUmrahOrder(item as UmrahMyOrder)
            case MyOrdersSection.insurance.key:
              return mapInsuranceOrder(item as InsuranceOrderDto)
            default:
              throw new Error(
                `Unhandled order category: ${String(group.category)}`,
              )
          }
        },
      )

      return { categoryLabel, cards }
    })
}
